<!-- 講座ページ -->
<script setup lang="ts">
  // タブ切り替え処理の説明
  // APIリクエスト数削減のためそれぞれのviewは最初にマウントしない。
  // ただし、毎回マウントではユーザービリティが悪いので一度表示したビューはマウントしっぱなしとする
  // v-if="planTabStore.isShowed(PlanTabMenuConfig.OVERVIEW.value)"の行で制御している
  // planTabStore.isShowedに一度表示したかどうかのフラグが入っている

  import { useElementBounding } from "@vueuse/core";

  import { PlanInfo } from "~/entities/PlanInfo";
  import PlanTabMenuConfig from "~/consts/PlanTabMenuConfig";
  import { usePlanTabStore } from "~/stores/PlanTabStore";

  import PlanMemberView from "~/components/Organisms/PlanMemberView.vue";
  import TalkView from "~/components/Organisms/TalkView.vue";
  import ContentView from "~/components/Organisms/ContentView.vue";

  interface Props {
    planInfo?: PlanInfo;
  }
  const props = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  // レスポンシブ対応
  const isSp = isSpDisplayRef();

  const planId = useRoute().params.id as string;

  // 自身が本講座のオーナーかどうか
  const isOwner = computed<boolean>(() => props.planInfo?.isOwner ?? false);

  // タブ状態保持
  const planTabStore = usePlanTabStore();
  // 戻るボタンで戻ってきた時に再フェッチしたいので、ここでtabを初期化する
  // この後にTabMenu.onMounted内でplanTabStore.setTabが呼ばれるので、queryで指定したタブに遷移できる
  planTabStore.resetTab();

  // 横スクロールがうまくいかないので親要素の幅を取得し子要素に渡す
  const planMemberViewParent = ref<HTMLDivElement | undefined>(undefined);
  const planMemberViewParentRect = reactive(useElementBounding(planMemberViewParent));

  // タブ切り替え時にデータ取得を行うので、refで管理する
  const planMemberView = ref<InstanceType<typeof PlanMemberView> | null>(null);
  const talkView = ref<InstanceType<typeof TalkView> | null>(null);
  const contentView = ref<InstanceType<typeof ContentView> | null>(null);

  // 表示するタブの状態
  const showTab = ref(planTabStore.getTab());

  // データ取得
  const fetchContents = (tabValue: string) => {
    switch (tabValue) {
      case PlanTabMenuConfig.CONTENT.value:
        contentView.value?.fetch();
        showTab.value = planTabStore.getTab();
        window.scrollTo(0, 0); // スクロール位置をリセット
        break;
      case PlanTabMenuConfig.MEMBER_LIST.value:
        showTab.value = planTabStore.getTab();
        planMemberView.value?.fetch();
        break;
      case PlanTabMenuConfig.TALK.value:
        // トークは即時タブを変更する
        talkView.value?.fetch();
        showTab.value = planTabStore.getTab();
        window.scrollTo(0, 0); // スクロール位置をリセット
        return;
      case PlanTabMenuConfig.OVERVIEW.value:
        showTab.value = planTabStore.getTab();
        return;
      default:
        // 上記以外は即時タブを変更する
        showTab.value = planTabStore.getTab();
        window.scrollTo(0, 0); // スクロール位置をリセット
    }
  };

  // iosのトークルームでスクロールできない不具合があるので、SP版は引っ張って更新を無効にする
  const changeScrollArea = (tabValue: string) => {
    const body = document.getElementsByTagName("body")[0];
    const html = document.getElementsByTagName("html")[0];
    switch (tabValue) {
      case PlanTabMenuConfig.TALK.value:
        // スクロール禁止
        if (!body.classList.contains("no_scroll")) {
          body.classList.add("no_scroll");
        }
        if (!html.classList.contains("no_scroll")) {
          html.classList.add("no_scroll");
        }
        break;
      default:
        // スクロール開放
        if (body.classList.contains("no_scroll")) {
          body.classList.remove("no_scroll");
        }
        if (html.classList.contains("no_scroll")) {
          html.classList.remove("no_scroll");
        }
    }
  };

  watch(
    () => {
      return planTabStore.getTab();
    },
    (newValue, _oldValue) => {
      fetchContents(newValue);
      changeScrollArea(newValue);
    },
  );

  const goOverView = () => {
    planTabStore.setTab("overview");
  };

  const route = useRoute();
  onMounted(() => {
    if (!route.query.tab) {
      // query tab=xxx がない場合は初期タブに変更する
      planTabStore.initTab();
    }
  });

  onUnmounted(() => {
    changeScrollArea("");
  });

  const computedPlanTab = computed({
    get: () => planTabStore.getTab(),
    set: value => {
      planTabStore.setTab(value);
    },
  });
</script>

<template>
  <v-sheet
    v-if="planInfo"
    :class="{ isFixedView: !isSp && ['talk', 'member_list', 'content'].includes(showTab) }"
    color="transparent"
    class="pa-0 plan-wrapper"
  >
    <OrganismsPlanTabMenu
      v-model:tab="computedPlanTab"
      :is-owner="isOwner"
      :talk-count="planInfo?.hasUnreadTalk ? 1 : 0"
      :talk-enabled="true"
      :content-count="
        planInfo.hasUnreadArchive ||
        planInfo.hasUnreadSchedule ||
        planInfo.hasUnreadLive ||
        planInfo.hasUnreadDocument ||
        planInfo.hasUnreadMovie ||
        planInfo.hasUnreadArchive ||
        planInfo.hasUnreadLibrary
          ? 1
          : 0
      "
      :content-enabled="true"
      class="plan_tab_menu"
    />
    <!-- overlayを使うので親要素にposition: relativeをセットする -->
    <v-sheet style="position: relative" color="transparent">
      <!-- 講座タブ選択時の描画 -->
      <div
        v-if="planTabStore.isShowed(PlanTabMenuConfig.OVERVIEW.value)"
        v-show="showTab === PlanTabMenuConfig.OVERVIEW.value"
        class="px-2"
      >
        <OrganismsPlanDetail v-if="planInfo" :plan-info="planInfo" />
      </div>
      <!-- トークルームタブ選択時の描画 -->
      <div v-if="planTabStore.isShowed(PlanTabMenuConfig.TALK.value)" v-show="showTab === PlanTabMenuConfig.TALK.value">
        <OrganismsTalkView ref="talkView" :plan-id="planId!" :plan-info="planInfo!" @go-over-view="goOverView()" />
      </div>
      <!-- コンテンツタブ選択時の描画 -->
      <div
        v-if="planTabStore.isShowed(PlanTabMenuConfig.CONTENT.value)"
        v-show="showTab === PlanTabMenuConfig.CONTENT.value"
      >
        <OrganismsContentView ref="contentView" :plan-info="planInfo" />
      </div>
      <!-- ユーザ一覧タブ選択時の描画 -->
      <div
        v-if="planTabStore.isShowed(PlanTabMenuConfig.MEMBER_LIST.value)"
        v-show="showTab === PlanTabMenuConfig.MEMBER_LIST.value"
        ref="planMemberViewParent"
      >
        <OrganismsPlanMemberView
          ref="planMemberView"
          :plan-info="planInfo"
          :parent-width="planMemberViewParentRect?.width"
        />
      </div>
    </v-sheet>
  </v-sheet>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .isFixedView {
    padding-bottom: 0px !important;
    // position: fixed;
    // height: 100vh;
    // @include display_pc {
    //   width: calc(100vw - 255px);
    // }
    // @media screen and (max-width: 1280px) {
    //   width: calc(100vw - 99px);
    // }
  }
  .plan-wrapper {
    padding-top: 54px !important;
    .plan_tab_menu {
      position: fixed;
      width: 100%;
      top: 76px;
      left: 0px;
      z-index: 100;
    }
  }
</style>
